import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	useGetListViewResponseMutation,
	useGetRemarksMutation,
	useUpdateProductPriorityMutation
} from '@/features/my-dashborad/api/myDashboardApi';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import { generateProductListParams } from '@/features/filter/static/filterUtil';

import ConstingListView from './list-table/ConstingListView';
import SampleJourneyList from './list-table/SampleJourneyList';
import GeneralListView from './list-table/GeneralListView';
import GrapichsListView from './list-table/GrapichsListView';
import FactoryDevelopmentList from './list-table/FactoryDevelopmentList';
import IssueListView from './list-table/IssueListView';
import ThreeDListView from './list-table/ThreeDListView';

const PAGE_SIZE = 20;

const MyDashboardListView = ({
	filterId,
	listViewType,
	selectedProducts = [],
	setSelectedProducts = () => {},
	onListDataUpdate = () => {},
	searchKey = '',
	selectedPriority = null,
	selectedGroupBy = null,
	refreshSignal = 0
}) => {
	const listType = String(listViewType ?? '').toLowerCase();
	const dispatch = useDispatch();
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isShowCosting, setIsShowCosting] = useState(false);
	const [selectedCostingIds, setSelectedCostingIds] = useState([]);
	const [mergedListViewResponse, setMergedListViewResponse] = useState(null);
	const [pageNumber, setPageNumber] = useState(0);
	const listViewRef = useRef();
	const listViewContainerRef = useRef(null);
	const tableScrollRef = useRef(null);
	const isFetchingRef = useRef(false);
	const isPageChangingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const filterfields = useSelector((store) => store.filterFieldStore);

	const [getListViewResponse, { data, isLoading: isFetching }] = useGetListViewResponseMutation();

	const [updateProductPriority, { isLoading: isUpdatingPriority }] = useUpdateProductPriorityMutation();
	const [getRemarks, { isLoading: isUpdatingRemarks }] = useGetRemarksMutation();

	//filter body
	const listViewRequestBody = useMemo(() => {
		const filterBody = generateProductListParams(filterfields);
		return {
			...filterBody,
			...(searchKey && { referenceNumber: searchKey }),
			...(selectedPriority?.id && { searchSortingOption: selectedPriority.id })
		};
	}, [filterfields, searchKey, selectedPriority]);

	const listViewRequestBodyKey = JSON.stringify(listViewRequestBody ?? {});

	const getHasMore = useCallback(
		(response, chunk, currentPage) => {
			// Check totalPages/currentPage (primary format)
			if (typeof response?.totalPages === 'number' && typeof response?.currentPage === 'number') {
				return response.currentPage + 1 < response.totalPages;
			}

			// Fallback to totalElements if available
			if (typeof response?.totalElements === 'number') {
				return (currentPage + 1) * PAGE_SIZE < response.totalElements;
			}

			// Last resort: check chunk size
			return chunk.length === PAGE_SIZE;
		},
		[listType]
	);

	const getMergeDedupeId = useCallback(
		(item) => {
			if (!listType.includes('costing')) return item?.id;
			return item?.costingJourneyDetailsResponse?.quoteItemResponse?.quantityWiseCostingId ?? item?.id;
		},
		[listType]
	);

	const listViewRequest = useMemo(
		() =>
			filterId
				? {
						id: filterId,
						params: { page: pageNumber, size: PAGE_SIZE },
						body: listViewRequestBody
					}
				: null,
		[filterId, pageNumber, listViewRequestBodyKey]
	);

	const buildMergedListViewResponse = useCallback(
		(response, currentPage, previousResponse = null) => {
			const chunk = response?.data ?? response?.content ?? [];
			const hasMore = getHasMore(response, chunk, currentPage);
			hasMoreRef.current = hasMore;

			if (currentPage === 0 || previousResponse == null) {
				return { ...response, data: chunk };
			}

			const existingIdMap = new Map(
				(previousResponse?.data ?? []).map((item) => [getMergeDedupeId(item), true])
			);
			const newRows = chunk.filter((item) => !existingIdMap.has(getMergeDedupeId(item)));

			return {
				...response,
				data: [...(previousResponse?.data ?? []), ...newRows]
			};
		},
		[getHasMore, getMergeDedupeId]
	);
	//refetch list view response
	const refetchListViewResponse = useCallback(async () => {
		if (!filterId) return null;

		const response = await getListViewResponse({
			id: filterId,
			params: { page: 0, size: PAGE_SIZE },
			body: listViewRequestBody
		}).unwrap();
		const next = buildMergedListViewResponse(response, 0, null);

		listViewRef.current = next;
		setMergedListViewResponse(next);
		setPageNumber(0);
		onListDataUpdate(next);

		return next;
	}, [filterId, listViewRequestBody, getListViewResponse, buildMergedListViewResponse, onListDataUpdate]);

	useEffect(() => {
		if (listViewRequest) getListViewResponse(listViewRequest);
	}, [listViewRequest]);

	// Reload the list after a successful product action triggered from the topbar action buttons
	useEffect(() => {
		if (refreshSignal === 0) return;
		refetchListViewResponse();
	}, [refreshSignal]);

	//update priority
	const handleUpdatePriority = async (priority) => {
		try {
			await updateProductPriority({
				productId: priority?.id,
				body: {
					priority: priority?.taskPriority
				}
			}).unwrap();
			await refetchListViewResponse();
			toastSuccess('Priority Updated Successfully!');
		} catch ({ error }) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	//remarks
	const handleGetRemarks = async (item, remarks) => {
		const details = item;
		const prevRemarks = (details?.remarks ?? '')?.trim();
		if (prevRemarks === remarks?.trim()) return;
		try {
			await getRemarks({
				id:
					details?.sampleItemId ||
					details?.inHouseDevelopmentJourneyDetails?.sampleItemId ||
					details?.factoryDevelopmentJourneyDetails?.sampleItemId,
				remarks: remarks
			}).unwrap();
			toastSuccess('Remarks updated successfully');
		} catch ({ error }) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	///pagination loader
	useEffect(() => {
		isFetchingRef.current = isFetching;
		if (!isFetching) {
			isPageChangingRef.current = false;
		}
	}, [isFetching]);

	const handleScroll = useCallback((wrappedElement) => {
		if (!wrappedElement || isFetchingRef.current || isPageChangingRef.current || !hasMoreRef.current)
			return;

		const { scrollHeight, scrollTop, clientHeight } = wrappedElement;
		if (scrollHeight - (scrollTop + clientHeight) <= 100) {
			isPageChangingRef.current = true;
			setPageNumber((prevPage) => prevPage + 1);
		}
	}, []);

	useEffect(() => {
		const wrappedElement = listViewContainerRef.current?.querySelector(
			'.list-view-table-container table.scroll-y-label'
		);

		tableScrollRef.current = wrappedElement ?? null;
		if (!wrappedElement) return undefined;

		const onScroll = () => handleScroll(wrappedElement);
		wrappedElement.addEventListener('scroll', onScroll);

		return () => wrappedElement.removeEventListener('scroll', onScroll);
	}, [handleScroll, listType, filterId]);

	useEffect(() => {
		if (tableScrollRef.current) {
			tableScrollRef.current.scrollTop = 0;
		}
		if (!filterId) {
			setMergedListViewResponse(null);
			listViewRef.current = null;
			setPageNumber(0);
			hasMoreRef.current = true;
			setSelectedProducts([]);
			setSelectedCostingIds([]);
			onListDataUpdate(null);
			return;
		}
		setPageNumber(0);
		hasMoreRef.current = true;
		setSelectedProducts([]);
		setSelectedCostingIds([]);
		onListDataUpdate(null);
	}, [filterId, listViewRequestBodyKey]);

	useEffect(() => {
		if (!filterId || data == null) return;

		setMergedListViewResponse((prev) => {
			const next = buildMergedListViewResponse(data, pageNumber, prev);
			listViewRef.current = next;
			onListDataUpdate(next);
			return next;
		});
	}, [filterId, data, pageNumber, buildMergedListViewResponse, onListDataUpdate]);

	//only for costinglistview table action button selection based on costing id
	useEffect(() => {
		if (!listType.includes('costing')) return;
		if (!Array.isArray(selectedCostingIds) || selectedCostingIds.length === 0) {
			setSelectedProducts([]);
			return;
		}

		const rows = mergedListViewResponse?.data ?? [];
		const selectedCostingIdSet = new Set(selectedCostingIds.map((id) => id?.toString()));
		const selectedProductIds = rows
			.filter((item) => {
				const quantityWiseCostingId =
					item?.costingJourneyDetailsResponse?.quoteItemResponse?.quantityWiseCostingId ?? item?.id;
				return selectedCostingIdSet.has(quantityWiseCostingId?.toString());
			})
			.map((item) => parseInt(item?.productId ?? item?.id))
			.filter((id) => !Number.isNaN(id));

		setSelectedProducts([...new Set(selectedProductIds)]);
	}, [listType, selectedCostingIds, mergedListViewResponse, setSelectedProducts]);

	//open techpack view modal
	useEffect(() => {
		if (selectedProductId !== null) {
			openDesignViewModal(selectedProductId);
		}
	}, [selectedProductId]);

	const openDesignViewModal = (selectedProductId) => {
		dispatch(
			setDesignViewModalInfo({
				id: selectedProductId,
				onCloseViewModal: () => {
					setSelectedProductId(null);
					setIsShowCosting(false);
					dispatch(clearDesignViewModalInfo({}));
				},
				onIssueResolved: () => refetchListViewResponse(),
				source: 'ORDER_DETAILS',
				isShowCostingPanel: isShowCosting,
				isShowCostingButton: true
			})
		);
	};

	const handleDesignImageClick = (productId) => {
		setIsShowCosting(false);
		setSelectedProductId(productId);
	};

	/// checkbox wise sample journey selection
	const handleSelectSampleItem = (e) => {
		const sampleId = e.target.value;
		if (e.target.checked) {
			setSelectedProducts((prev) => [...new Set([...prev, parseInt(sampleId)])]);
		} else {
			setSelectedProducts((prev) => prev.filter((id) => id !== parseInt(sampleId)));
		}
	};

	const handleSelectAllSampleItem = (e) => {
		const sampleList = mergedListViewResponse?.data ?? [];
		const allSampleIds = sampleList.map((item) => parseInt(item?.id)).filter((id) => !Number.isNaN(id));
		if (e.target.checked) {
			setSelectedProducts((prev) => [...new Set([...prev, ...allSampleIds])]);
		} else {
			setSelectedProducts((prev) => prev.filter((id) => !allSampleIds.includes(id)));
		}
	};

	const handleSelectGroupSampleItem = (e, items = []) => {
		const groupSampleIds = items.map((item) => parseInt(item?.id)).filter((id) => !Number.isNaN(id));
		if (e.target.checked) {
			setSelectedProducts((prev) => [...new Set([...prev, ...groupSampleIds])]);
		} else {
			setSelectedProducts((prev) => prev.filter((id) => !groupSampleIds.includes(id)));
		}
	};

	const renderListView = (listViewResponse) => {
		switch (true) {
			case listType.includes('sample_in_house_development'):
				return (
					<SampleJourneyList
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						selectedSampleItem={selectedProducts}
						handleUpdatePriority={handleUpdatePriority}
						handleGetRemarks={handleGetRemarks}
						isUpdatingPriority={isUpdatingPriority}
						isUpdatingRemarks={isUpdatingRemarks}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('sample_factory_development'):
				return (
					<FactoryDevelopmentList
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						selectedSampleItem={selectedProducts}
						handleUpdatePriority={handleUpdatePriority}
						handleGetRemarks={handleGetRemarks}
						isUpdatingPriority={isUpdatingPriority}
						isUpdatingRemarks={isUpdatingRemarks}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('costing'):
				return (
					<ConstingListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						selectedIds={selectedCostingIds}
						setSelectedIds={setSelectedCostingIds}
						onRefreshList={refetchListViewResponse}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('issue'):
				return (
					<IssueListView
						listViewResponse={listViewResponse}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						onClickDesignImage={handleDesignImageClick}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('graphics'):
				return (
					<GrapichsListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						selectedSampleItem={selectedProducts}
						refetchList={refetchListViewResponse}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			case listType.includes('three_d'):
				return (
					<ThreeDListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
			default:
				return (
					<GeneralListView
						listViewResponse={listViewResponse}
						onClickDesignImage={handleDesignImageClick}
						handleSelectSampleItem={handleSelectSampleItem}
						handleSelectAllSampleItem={handleSelectAllSampleItem}
						handleSelectGroupSampleItem={handleSelectGroupSampleItem}
						selectedSampleItem={selectedProducts}
						selectedGroupBy={selectedGroupBy}
					/>
				);
		}
	};
	return (
		<div ref={listViewContainerRef}>
			<LoaderComponent loading={isFetching || isUpdatingPriority || isUpdatingRemarks}>
				{renderListView(mergedListViewResponse?.data ?? [])}
			</LoaderComponent>
		</div>
	);
};
export default MyDashboardListView;
